import { requestApi } from "../api/client.js";
import type { PlanInstance } from "../plan/plan-api.js";

export interface Registration {
  authority_kind: "council" | "mpi";
  authority_name: string;
  expires_on: string | null;
  id: string;
  locations: {
    effective_from: string;
    effective_to: string | null;
    id: string;
    location_id: string;
  }[];
  registered_on: string | null;
  registration_no: string | null;
  status: "active" | "cancelled" | "lapsed" | "pending";
}

export interface RegistrationInput {
  authority_kind: Registration["authority_kind"];
  authority_name: string;
  expires_on: string | null;
  registered_on: string | null;
  registration_no: string | null;
}

export type VisitOutcome =
  "acceptable" | "improvement_required" | "other" | "unacceptable";

export const visitOutcomeLabels: Record<VisitOutcome, string> = {
  acceptable: "Acceptable",
  improvement_required: "Improvement required",
  other: "Other",
  unacceptable: "Unacceptable",
};

export interface VisitInput {
  next_due_on: string | null;
  occurred_on: string;
  outcome: VisitOutcome | null;
  verifier_name: string | null;
}

export const MPI_NAME = "Ministry for Primary Industries";

// The live (active or pending) registration that covers this location now;
// the latest if several do. A cancelled or lapsed one starts a new form.
export function currentRegistration(
  registrations: readonly Registration[],
  locationId: string,
): Registration | null {
  const covering = registrations.filter(
    (registration) =>
      (registration.status === "active" || registration.status === "pending") &&
      registration.locations.some(
        (entry) =>
          entry.location_id === locationId && entry.effective_to === null,
      ),
  );
  return (
    covering.sort((a, b) =>
      (b.registered_on ?? "").localeCompare(a.registered_on ?? ""),
    )[0] ?? null
  );
}

export function listRegistrations(): Promise<Registration[]> {
  return requestApi<Registration[]>("/registrations");
}

export function saveRegistration(
  input: RegistrationInput,
  locationId: string,
  existing: Registration | null,
): Promise<Registration> {
  if (existing)
    return requestApi<Registration>(`/registrations/${existing.id}`, {
      body: JSON.stringify(input),
      method: "PATCH",
    });
  return requestApi<Registration>("/registrations", {
    body: JSON.stringify({ ...input, location_ids: [locationId] }),
    method: "POST",
  });
}

export interface VerificationVisit {
  id: string;
  next_due_on: string | null;
  occurred_on: string | null;
  outcome: VisitOutcome | null;
  scheduled_on: string | null;
  verifier_name: string | null;
}

// Newest first, including visits for a registration that covers or covered
// the location.
export async function listVerificationVisits(
  locationId: string,
): Promise<VerificationVisit[]> {
  const page = await requestApi<{ items: VerificationVisit[] }>(
    `/verification-events?location_id=${encodeURIComponent(locationId)}`,
  );
  return page.items;
}

const visitDate = new Intl.DateTimeFormat("en-NZ", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
  year: "numeric",
});

// "20 Oct 2026 · Acceptable · Sam Verifier"
export function visitSummary(visit: VerificationVisit): string {
  const day = visit.occurred_on ?? visit.scheduled_on;
  return [
    day ? visitDate.format(new Date(`${day}T00:00:00Z`)) : null,
    visit.outcome ? visitOutcomeLabels[visit.outcome] : null,
    visit.verifier_name,
  ]
    .filter(Boolean)
    .join(" · ");
}

export function addVerificationVisit(
  input: VisitInput,
  registrationId: string,
  locationId: string,
): Promise<unknown> {
  return requestApi("/verification-events", {
    body: JSON.stringify({
      ...input,
      location_id: locationId,
      registration_id: registrationId,
    }),
    method: "POST",
  });
}

// Plan answers the verification report reads for its first page. The keys
// are the first ones the report looks for.
export interface BusinessDetails {
  activities: string[];
  day_to_day_manager: string;
  operator_name: string;
}

function text(value: unknown): string {
  return typeof value === "string" ? value : "";
}

export function businessDetails(
  answers: Readonly<Record<string, unknown>>,
): BusinessDetails {
  const activities =
    answers.activities ??
    answers.business_activities ??
    answers.food_activities;
  return {
    activities: Array.isArray(activities)
      ? activities.filter((item): item is string => typeof item === "string")
      : typeof activities === "string" && activities
        ? [activities]
        : [],
    day_to_day_manager:
      text(answers.day_to_day_manager) ||
      text(answers.day_to_day_manager_name) ||
      text(answers.manager_name),
    operator_name: text(answers.operator_name) || text(answers.operator),
  };
}

// One activity per line.
export function activitiesFromText(value: string): string[] {
  return value
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean);
}

// The operator, day-to-day manager and food activities change in place on
// the published plan (PUT /plan-instances/{id}/answers): no new version, no
// review and no new checks. Plan changes left open get them too, so
// publishing those later keeps them.
export async function saveBusinessDetails(
  details: BusinessDetails,
  instances: readonly PlanInstance[],
): Promise<void> {
  const targets = instances.filter(
    (instance) => instance.state === "published" || instance.state === "draft",
  );
  if (targets.length === 0) throw new Error("This location has no plan yet.");
  await Promise.all(
    targets.map((instance) =>
      requestApi<PlanInstance>(`/plan-instances/${instance.id}/answers`, {
        body: JSON.stringify(details),
        method: "PUT",
      }),
    ),
  );
}
